import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { Page } from ".";
import { match } from "ts-pattern";
import { SecondaryButton } from "../Button";
import { UnstyledButton } from "../Button/UnstalyedButton";
import { Tooltip } from "../Tooltip";
import { DivLink } from "../Link";
import { IconX, IconDots } from "../icons";
import { createTestId } from "../TestableElement";
import { useWindowSizeBiggerOrEqualTo } from "../utils/useWindowSizeBreakpoint";

const iconButtonClassName =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md cursor-pointer text-content-dimmed hover:text-content-base hover:bg-surface-dimmed focus-visible:text-content-base focus-visible:bg-surface-dimmed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-1";

export function PageOptions({ options, testId = "options-button" }: { options?: Page.Option[]; testId?: string }) {
  const [open, setOpen] = React.useState(false);
  const close = () => setOpen(false);
  const isBigScreen = useWindowSizeBiggerOrEqualTo("lg");

  if (!options) {
    return null;
  }

  const visibleOptions = options.filter((option) => !option.hidden);

  if (visibleOptions.length === 0) {
    return null;
  }

  const [outsideOptions, insideOptions] = splitOptions(isBigScreen, visibleOptions);

  return (
    <div className="absolute right-0 top-0">
      <div className="absolute right-2.5 top-2.5 flex items-center gap-2">
        <OutsideButtons options={outsideOptions} />

        {insideOptions.length > 0 && (
          <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
            <Trigger testId={testId} />
            <Content close={close} options={insideOptions} />
          </DropdownMenu.Root>
        )}
      </div>
    </div>
  );
}

function OutsideButtons({ options }: { options: Page.Option[] }) {
  if (options.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      {options.map((option, index) =>
        option.iconOnly ? (
          <OutsideIcon key={index} {...option} />
        ) : (
          match(option.type)
            .with("link", () => <OutsideLink key={index} {...option} />)
            .with("action", () => <OutsideAction key={index} {...option} />)
            .exhaustive()
        ),
      )}
    </div>
  );
}

function OutsideIcon(props: Page.Option) {
  return (
    <Tooltip content={props.label} size="sm">
      <span className="flex">
        <UnstyledButton
          linkTo={props.type === "link" ? props.link : undefined}
          onClick={props.type === "action" ? props.onClick : undefined}
          ariaLabel={props.label}
          testId={props.testId ?? "page-option-" + createTestId(props.label)}
          className={iconButtonClassName}
        >
          {React.createElement(props.icon, { size: 22, "aria-hidden": true })}
          <span className="sr-only">{props.label}</span>
        </UnstyledButton>
      </span>
    </Tooltip>
  );
}

function OutsideLink(props: Page.Option) {
  return (
    <SecondaryButton size="xs" linkTo={props.link!} testId={props.testId ?? "page-option-" + createTestId(props.label)}>
      {props.label}
    </SecondaryButton>
  );
}

function OutsideAction(props: Page.Option) {
  return (
    <SecondaryButton
      size="xs"
      onClick={props.onClick}
      testId={props.testId ?? "page-option-" + createTestId(props.label)}
    >
      {props.label}
    </SecondaryButton>
  );
}

function Trigger({ testId }: { testId: string }) {
  return (
    <DropdownMenu.Trigger className={iconButtonClassName} data-test-id={testId}>
      <IconDots size={22} />
    </DropdownMenu.Trigger>
  );
}

function Content({ close, options }: { close: () => void; options: Page.Option[] }) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        className="z-50 shadow-lg bg-accent-1 w-[300px] text-white-1 font-medium flex flex-col"
        align="end"
        alignOffset={-10}
        sideOffset={-40}
      >
        <Close onClick={close} />
        <Items options={options} close={close} />
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

function Items({ options, close }: { options: Page.Option[]; close: () => void }): React.ReactElement[] {
  return options.map((option, index) =>
    match(option.type)
      .with("link", () => <Link {...option} key={index} />)
      .with("action", () => <Action {...option} key={index} close={close} />)
      .exhaustive(),
  );
}

function Link(props: Page.Option) {
  return (
    <DropdownMenu.Item>
      <DivLink
        to={props.link!}
        className="flex items-center gap-2 py-2 px-4 hover:bg-shade-1 cursor-pointer"
        testId={props.testId ?? "page-option-" + createTestId(props.label)}
      >
        {React.createElement(props.icon, { size: 20 })}
        {props.label}
      </DivLink>
    </DropdownMenu.Item>
  );
}

function Action(props: Page.Option & { close: () => void }) {
  const handleClick = () => {
    props.onClick?.();
    props.close();
  };

  return (
    <DropdownMenu.Item onSelect={handleClick}>
      <div
        className="flex items-center gap-2 py-2 px-4 hover:bg-shade-1 cursor-pointer"
        data-test-id={props.testId ?? "page-option-" + createTestId(props.label)}
      >
        {React.createElement(props.icon, { size: 20 })}
        {props.label}
      </div>
    </DropdownMenu.Item>
  );
}

function Close({ onClick }) {
  return (
    <div className="flex flex-row-reverse m-4 cursor-pointer">
      <div
        className="p-1 rounded-full border border-content-base"
        onClick={onClick}
        data-test-id="project-options-button"
      >
        <IconX size={20} />
      </div>
    </div>
  );
}

function splitOptions(isBigScreen: boolean, options: Page.Option[]): [Page.Option[], Page.Option[]] {
  const outside: Page.Option[] = [];
  const inside: Page.Option[] = [];

  for (const option of options) {
    if (option.keepOutside || (isBigScreen && option.keepOutsideOnBigScreen)) {
      outside.push(option);
    } else {
      inside.push(option);
    }
  }

  return [outside, inside];
}
